/**
 * useScrollBehavior Hook
 *
 * Manages scroll refs and behavior for the chat widget.
 * Provides utilities for scrolling to bottom and scrolling to latest question.
 */

import { useCallback, useRef } from 'react';

interface UseScrollBehaviorReturn {
    messagesEndRef: React.RefObject<HTMLDivElement>;
    latestQuestionRef: React.MutableRefObject<HTMLElement | null>;
    scrollToBottom: () => void;
    scrollToLatestQuestion: () => void;
}

/**
 * Hook for managing scroll behavior in the chat widget
 *
 * @returns Scroll refs and scroll functions
 *
 * @example
 * const { messagesEndRef, scrollToBottom, scrollToLatestQuestion } = useScrollBehavior();
 *
 * // Attach refs to elements
 * <div ref={messagesEndRef} />
 *
 * // Scroll programmatically
 * scrollToBottom();
 * scrollToLatestQuestion();
 */
export function useScrollBehavior(): UseScrollBehaviorReturn {
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const latestQuestionRef = useRef<HTMLElement | null>(null);

    /**
     * Scroll to the bottom of the chat messages
     * Used after sending a message or receiving a response
     */
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    /**
     * Scroll to the latest question in the conversation
     * Used for navigation within long conversations
     */
    const scrollToLatestQuestion = useCallback(() => {
        if (latestQuestionRef.current) {
            latestQuestionRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }, []);

    return {
        messagesEndRef,
        latestQuestionRef,
        scrollToBottom,
        scrollToLatestQuestion,
    };
}
